import { action, computed, makeObservable, observable } from "mobx";
import dayjs, { Dayjs } from "dayjs";
import SwayyCouponAPI from "../api/SwayyCouponAPI";
import {
  CreateSwayyCouponPayload,
  GetSwayyCouponsParams,
  SwayyCoupon,
  SwayyCouponActivityLog,
  SwayyCouponChannel,
  SwayyCouponDiscountType,
  SwayyCouponRedemption,
  SwayyCouponsPagination,
  UpdateSwayyCouponPayload,
  swayyCouponDateToDayjs,
} from "../../../models/SwayyCoupon/SwayyCoupon";
import {
  isSwayyCouponState,
  SWAYY_COUPON_STATUS_ALL_TAB,
  SWAYY_COUPON_STATUS_DEFAULT_TAB,
} from "../../../models/SwayyCoupon/SwayyCouponFilters";
import { getSwayyCouponAccess } from "../../../models/SwayyCoupon/SwayyCouponPermissions";
import { userStore } from "../../../stores/sharedStores";

const parseStatusParam = (value: string | null): string => {
  if (!value) return SWAYY_COUPON_STATUS_DEFAULT_TAB;
  if (value === SWAYY_COUPON_STATUS_ALL_TAB) return "";
  return isSwayyCouponState(value) ? value : SWAYY_COUPON_STATUS_DEFAULT_TAB;
};

const parseDateParam = (value: string | null): string => {
  if (!value) return "";
  const date = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
};

export interface SwayyCouponFormValues {
  code: string;
  description: string;
  discountType: SwayyCouponDiscountType;
  discountValue: string;
  maxDiscount: string;
  minOrderValue: string;
  channel: SwayyCouponChannel;
  usageLimit: string;
  usagePerUser: string;
  firstOrderOnly: boolean;
  isActive: boolean;
  isExpired: boolean;
  startsAt: Dayjs | null;
  endsAt: Dayjs | null;
}

export const EMPTY_SWAYY_COUPON_FORM: SwayyCouponFormValues = {
  code: "",
  description: "",
  discountType: "percent",
  discountValue: "10",
  maxDiscount: "",
  minOrderValue: "0",
  channel: "b2c",
  usageLimit: "",
  usagePerUser: "1",
  firstOrderOnly: false,
  isActive: true,
  isExpired: false,
  startsAt: null,
  endsAt: null,
};

const emptyCreateForm = (): SwayyCouponFormValues => ({
  ...EMPTY_SWAYY_COUPON_FORM,
  startsAt: dayjs().startOf("day"),
});

const formFromCoupon = (coupon: SwayyCoupon): SwayyCouponFormValues => ({
  code: coupon.code,
  description: coupon.description ?? "",
  discountType: coupon.discount_type,
  discountValue: String(coupon.discount_value ?? 0),
  maxDiscount:
    coupon.max_discount != null ? String(coupon.max_discount) : "",
  minOrderValue: String(coupon.min_order_value ?? 0),
  channel: coupon.channel,
  usageLimit: coupon.usage_limit != null ? String(coupon.usage_limit) : "",
  usagePerUser: String(coupon.usage_per_user ?? 1),
  firstOrderOnly: coupon.first_order_only,
  isActive: coupon.is_active,
  isExpired: coupon.is_expired,
  startsAt: coupon.starts_at ? swayyCouponDateToDayjs(coupon.starts_at) : null,
  endsAt: coupon.ends_at ? swayyCouponDateToDayjs(coupon.ends_at) : null,
});

const DEFAULT_SWAYY_COUPON_LIST_PARAMS: GetSwayyCouponsParams = {
  page: 1,
  limit: 20,
  status: SWAYY_COUPON_STATUS_DEFAULT_TAB,
  q: "",
  channel: "",
  starts_from: "",
  starts_to: "",
  ends_from: "",
  ends_to: "",
};

class SwayyCouponStore {
  swayyCouponAPI: SwayyCouponAPI;
  coupons: SwayyCoupon[];
  pagination: SwayyCouponsPagination;
  params: GetSwayyCouponsParams;
  filtersOpen: boolean;
  draftQ: string;
  draftChannel: string[];
  draftStartsFrom: string;
  draftStartsTo: string;
  draftEndsFrom: string;
  draftEndsTo: string;
  loading: boolean;
  error: string | null;
  detail: SwayyCoupon | null;
  redemptions: SwayyCouponRedemption[];
  activityLogs: SwayyCouponActivityLog[];
  activityLogsOpen: boolean;
  activityLogsLoading: boolean;
  activityLogsError: string | null;
  activityLogsTotal: number;
  activityLogsOffset: number;
  activityLogsLimit: number;
  detailLoading: boolean;
  detailOpen: boolean;
  createOpen: boolean;
  editingCoupon: SwayyCoupon | null;
  form: SwayyCouponFormValues;
  formError: string;
  saving: boolean;
  selectedIds: number[];
  urlReady: boolean;
  expireOpen: boolean;
  actionsMenuOpen: boolean;
  expandedActivityLogIds: number[];
  private fetchGeneration: number;

  constructor() {
    this.swayyCouponAPI = new SwayyCouponAPI();
    this.coupons = [];
    this.pagination = { total: 0, page: 1, limit: 20 };
    this.params = { ...DEFAULT_SWAYY_COUPON_LIST_PARAMS };
    this.filtersOpen = false;
    this.draftQ = "";
    this.draftChannel = [];
    this.draftStartsFrom = "";
    this.draftStartsTo = "";
    this.draftEndsFrom = "";
    this.draftEndsTo = "";
    this.loading = false;
    this.error = null;
    this.detail = null;
    this.redemptions = [];
    this.activityLogs = [];
    this.activityLogsOpen = false;
    this.activityLogsLoading = false;
    this.activityLogsError = null;
    this.activityLogsTotal = 0;
    this.activityLogsOffset = 0;
    this.activityLogsLimit = 20;
    this.detailLoading = false;
    this.detailOpen = false;
    this.createOpen = false;
    this.editingCoupon = null;
    this.form = emptyCreateForm();
    this.formError = "";
    this.saving = false;
    this.selectedIds = [];
    this.urlReady = false;
    this.expireOpen = false;
    this.actionsMenuOpen = false;
    this.expandedActivityLogIds = [];
    this.fetchGeneration = 0;

    makeObservable(this, {
      coupons: observable,
      pagination: observable,
      params: observable,
      filtersOpen: observable,
      draftQ: observable,
      draftChannel: observable,
      draftStartsFrom: observable,
      draftStartsTo: observable,
      draftEndsFrom: observable,
      draftEndsTo: observable,
      loading: observable,
      error: observable,
      detail: observable,
      redemptions: observable,
      activityLogs: observable,
      activityLogsOpen: observable,
      activityLogsLoading: observable,
      activityLogsError: observable,
      activityLogsTotal: observable,
      activityLogsOffset: observable,
      activityLogsLimit: observable,
      activityLogsPage: computed,
      activityLogsTotalPages: computed,
      detailLoading: observable,
      detailOpen: observable,
      createOpen: observable,
      editingCoupon: observable,
      form: observable.ref,
      formError: observable,
      saving: observable,
      selectedIds: observable,
      urlReady: observable,
      expireOpen: observable,
      actionsMenuOpen: observable,
      expandedActivityLogIds: observable,
      statusTab: computed,
      selectedCount: computed,
      allPageSelected: computed,
      somePageSelected: computed,
      getCoupons: action,
      getCouponDetail: action,
      createCoupon: action,
      updateCoupon: action,
      bulkExpireCoupons: action,
      closeDetail: action,
      openActivityLogs: action,
      closeActivityLogs: action,
      loadActivityLogs: action,
      openCreate: action,
      openEdit: action,
      closeCreate: action,
      patchForm: action,
      setFormError: action,
      resetForm: action,
      openExpireConfirm: action,
      closeExpireConfirm: action,
      openActionsMenu: action,
      closeActionsMenu: action,
      toggleActivityLogExpanded: action,
      resetListPageUi: action,
      toggleSelected: action,
      toggleSelectAllOnPage: action,
      clearSelection: action,
      setStatusTab: action,
      toggleFiltersOpen: action,
      setDraftQ: action,
      setDraftChannel: action,
      setDraftStartDateRange: action,
      setDraftEndDateRange: action,
      applyFilters: action,
      clearFilters: action,
      handlePageChange: action,
      handleRowsPerPageChange: action,
      initializeParamsFromSearch: action,
      buildSearchParams: action,
    });
  }

  get statusTab(): string {
    const status = this.params.status || "";
    return isSwayyCouponState(status) ? status : SWAYY_COUPON_STATUS_ALL_TAB;
  }

  get selectedCount(): number {
    return this.selectedIds.length;
  }

  get allPageSelected(): boolean {
    return (
      this.coupons.length > 0 &&
      this.coupons.every((coupon) => this.selectedIds.includes(coupon.id))
    );
  }

  get somePageSelected(): boolean {
    if (this.allPageSelected) return false;
    return this.coupons.some((coupon) => this.selectedIds.includes(coupon.id));
  }

  get activityLogsPage(): number {
    return Math.floor(this.activityLogsOffset / this.activityLogsLimit) + 1;
  }

  get activityLogsTotalPages(): number {
    if (this.activityLogsTotal <= 0) return 0;
    return Math.ceil(this.activityLogsTotal / this.activityLogsLimit);
  }

  isSelected = (couponId: number): boolean =>
    this.selectedIds.includes(couponId);

  getCoupons = async () => {
    const requestParams: GetSwayyCouponsParams = { ...this.params };
    const generation = ++this.fetchGeneration;
    this.loading = true;
    this.error = null;
    try {
      const response = await this.swayyCouponAPI.getCoupons(requestParams);
      if (generation !== this.fetchGeneration) return null;
      this.coupons = response.coupons;
      this.pagination = response.pagination;
      return response;
    } catch (error: any) {
      if (generation !== this.fetchGeneration) return null;
      this.error = error?.message || "Failed to fetch coupons";
      throw error;
    } finally {
      if (generation === this.fetchGeneration) {
        this.loading = false;
      }
    }
  };

  getCouponDetail = async (couponId: number | string) => {
    this.detailLoading = true;
    this.detailOpen = true;
    this.redemptions = [];
    this.resetActivityLogs();
    try {
      const [detail, redemptions] = await Promise.all([
        this.swayyCouponAPI.getCoupon(couponId),
        this.swayyCouponAPI.getCouponRedemptions(couponId),
      ]);
      this.detail = detail;
      this.redemptions = redemptions.data;
      return detail;
    } catch (error: any) {
      this.detail = null;
      throw error;
    } finally {
      this.detailLoading = false;
    }
  };

  openActivityLogs = () => {
    if (!this.detail) return;
    this.activityLogsOpen = true;
    void this.loadActivityLogs(0);
  };

  closeActivityLogs = () => {
    this.activityLogsOpen = false;
  };

  loadActivityLogs = async (offset = 0) => {
    const couponId = this.detail?.id;
    if (!couponId) return;

    this.activityLogsLoading = true;
    this.activityLogsError = null;
    try {
      const activity = await this.swayyCouponAPI.getCouponActivityLogs(
        couponId,
        this.activityLogsLimit,
        offset,
      );
      this.activityLogs = activity.data;
      this.activityLogsTotal = activity.pagination.total;
      this.activityLogsLimit = activity.pagination.limit;
      this.activityLogsOffset = activity.pagination.offset;
      this.expandedActivityLogIds = [];
    } catch (error: any) {
      this.activityLogs = [];
      this.activityLogsTotal = 0;
      this.activityLogsError =
        error?.message || "Failed to load coupon activity.";
    } finally {
      this.activityLogsLoading = false;
    }
  };

  private resetActivityLogs = () => {
    this.activityLogsOpen = false;
    this.activityLogs = [];
    this.activityLogsLoading = false;
    this.activityLogsError = null;
    this.activityLogsTotal = 0;
    this.activityLogsOffset = 0;
    this.expandedActivityLogIds = [];
  };

  private refreshOpenActivityLogs = async () => {
    if (!this.activityLogsOpen || !this.detail) return;
    await this.loadActivityLogs(0);
  };

  createCoupon = async (body: CreateSwayyCouponPayload) => {
    if (!getSwayyCouponAccess(userStore.permissionMap).canCreate) {
      throw new Error("You do not have permission to create coupons.");
    }
    this.saving = true;
    try {
      const created = await this.swayyCouponAPI.createCoupon(body);
      this.resetForm();
      this.createOpen = false;
      await this.getCoupons();
      return created;
    } finally {
      this.saving = false;
    }
  };

  updateCoupon = async (
    couponId: number | string,
    body: UpdateSwayyCouponPayload,
  ) => {
    this.saving = true;
    try {
      const updated = await this.swayyCouponAPI.updateCoupon(couponId, body);
      this.detail = updated;
      this.coupons = this.coupons.map((coupon) =>
        coupon.id === updated.id ? updated : coupon,
      );
      await this.refreshOpenActivityLogs();
      return updated;
    } finally {
      this.saving = false;
    }
  };

  closeDetail = () => {
    this.detailOpen = false;
    this.detail = null;
    this.redemptions = [];
    this.resetActivityLogs();
    this.detailLoading = false;
  };

  openCreate = () => {
    if (!getSwayyCouponAccess(userStore.permissionMap).canCreate) {
      this.createOpen = false;
      this.editingCoupon = null;
      this.resetForm();
      return;
    }
    this.editingCoupon = null;
    this.resetForm();
    this.createOpen = true;
    this.actionsMenuOpen = false;
  };

  openEdit = (coupon: SwayyCoupon) => {
    this.createOpen = false;
    this.editingCoupon = coupon;
    this.form = formFromCoupon(coupon);
    this.formError = "";
  };

  closeCreate = () => {
    this.createOpen = false;
    this.editingCoupon = null;
    this.resetForm();
  };

  resetForm = () => {
    this.form = emptyCreateForm();
    this.formError = "";
  };

  patchForm = (next: Partial<SwayyCouponFormValues>) => {
    this.form = { ...this.form, ...next };
  };

  setFormError = (message: string) => {
    this.formError = message;
  };

  openActionsMenu = () => {
    this.actionsMenuOpen = true;
  };

  closeActionsMenu = () => {
    this.actionsMenuOpen = false;
  };

  openExpireConfirm = () => {
    this.actionsMenuOpen = false;
    this.expireOpen = true;
  };

  closeExpireConfirm = () => {
    if (this.saving) return;
    this.expireOpen = false;
  };

  isActivityLogExpanded = (logId: number): boolean =>
    this.expandedActivityLogIds.includes(logId);

  toggleActivityLogExpanded = (logId: number) => {
    this.expandedActivityLogIds = this.expandedActivityLogIds.includes(logId)
      ? this.expandedActivityLogIds.filter((id) => id !== logId)
      : [...this.expandedActivityLogIds, logId];
  };

  resetListPageUi = () => {
    this.urlReady = false;
    this.actionsMenuOpen = false;
    this.expireOpen = false;
  };

  setStatusTab = (statusId: string) => {
    const status =
      !statusId || statusId === SWAYY_COUPON_STATUS_ALL_TAB ? "" : statusId;
    if ((this.params.status || "") === status) return;

    this.coupons = [];
    this.pagination = { ...this.pagination, total: 0, page: 1 };
    this.params = {
      ...this.params,
      status,
      page: 1,
    };
    this.clearSelection();
  };

  initializeParamsFromSearch = (searchParams: URLSearchParams) => {
    const page = Math.max(
      1,
      parseInt(searchParams.get("page") || "1", 10) || 1,
    );
    const parsedLimit = parseInt(searchParams.get("limit") || "20", 10);
    const limit = [10, 20, 50, 100].includes(parsedLimit) ? parsedLimit : 20;
    const rawStatus = searchParams.get("status");
    const status = parseStatusParam(rawStatus);
    const q = searchParams.get("q") || "";
    const channel = searchParams.get("channel") || "";
    const startsFrom = parseDateParam(searchParams.get("starts_from"));
    const startsTo = parseDateParam(searchParams.get("starts_to"));
    const endsFrom = parseDateParam(searchParams.get("ends_from"));
    const endsTo = parseDateParam(searchParams.get("ends_to"));

    this.params = {
      page,
      limit,
      status,
      q,
      channel,
      starts_from: startsFrom,
      starts_to: startsTo,
      ends_from: endsFrom,
      ends_to: endsTo,
    };
    this.draftQ = q;
    this.draftChannel = channel ? [channel] : [];
    this.draftStartsFrom = startsFrom;
    this.draftStartsTo = startsTo;
    this.draftEndsFrom = endsFrom;
    this.draftEndsTo = endsTo;
    this.urlReady = true;
  };

  buildSearchParams = (): URLSearchParams => {
    const searchParams = new URLSearchParams();
    const { params } = this;

    if (params.page > 1) searchParams.set("page", String(params.page));
    if (params.limit && params.limit !== 20) {
      searchParams.set("limit", String(params.limit));
    }
    searchParams.set("status", params.status || SWAYY_COUPON_STATUS_ALL_TAB);
    if (params.q) searchParams.set("q", params.q);
    if (params.channel) searchParams.set("channel", params.channel);
    if (params.starts_from) searchParams.set("starts_from", params.starts_from);
    if (params.starts_to) searchParams.set("starts_to", params.starts_to);
    if (params.ends_from) searchParams.set("ends_from", params.ends_from);
    if (params.ends_to) searchParams.set("ends_to", params.ends_to);

    return searchParams;
  };

  toggleFiltersOpen = () => {
    this.filtersOpen = !this.filtersOpen;
  };

  setDraftQ = (value: string) => {
    this.draftQ = value;
  };

  setDraftChannel = (channel: string[]) => {
    this.draftChannel = channel;
  };

  setDraftStartDateRange = (fromDate: string, toDate: string) => {
    this.draftStartsFrom = fromDate;
    this.draftStartsTo = toDate;
  };

  setDraftEndDateRange = (fromDate: string, toDate: string) => {
    this.draftEndsFrom = fromDate;
    this.draftEndsTo = toDate;
  };

  applyFilters = () => {
    const startFrom = this.draftStartsFrom;
    const startTo = this.draftStartsTo;
    const endFrom = this.draftEndsFrom;
    const endTo = this.draftEndsTo;
    const startIsSingleDay = Boolean(startFrom && !startTo);
    const endIsSingleDay = Boolean(endFrom && !endTo);
    const isStartDateRange =
      startIsSingleDay && endIsSingleDay;

    const starts_from = startFrom;
    const starts_to = isStartDateRange ? endFrom : startTo || startFrom;
    const ends_from = isStartDateRange ? "" : endFrom;
    const ends_to = isStartDateRange ? "" : endTo || endFrom;

    this.params = {
      ...this.params,
      q: this.draftQ.trim(),
      channel: this.draftChannel[0] || "",
      starts_from,
      starts_to,
      ends_from,
      ends_to,
      page: 1,
    };
    this.clearSelection();
  };

  clearFilters = () => {
    this.draftQ = "";
    this.draftChannel = [];
    this.draftStartsFrom = "";
    this.draftStartsTo = "";
    this.draftEndsFrom = "";
    this.draftEndsTo = "";
    this.params = {
      ...DEFAULT_SWAYY_COUPON_LIST_PARAMS,
      limit: this.params.limit,
      status: this.params.status,
    };
    this.clearSelection();
  };

  handlePageChange = (page: number) => {
    this.params = { ...this.params, page };
  };

  handleRowsPerPageChange = (limit: number) => {
    this.params = { ...this.params, limit, page: 1 };
  };

  toggleSelected = (couponId: number) => {
    this.selectedIds = this.selectedIds.includes(couponId)
      ? this.selectedIds.filter((id) => id !== couponId)
      : [...this.selectedIds, couponId];
  };

  toggleSelectAllOnPage = () => {
    const pageIds = this.coupons.map((coupon) => coupon.id);
    if (this.allPageSelected) {
      this.selectedIds = this.selectedIds.filter(
        (id) => !pageIds.includes(id),
      );
      return;
    }
    this.selectedIds = Array.from(new Set([...this.selectedIds, ...pageIds]));
  };

  clearSelection = () => {
    this.selectedIds = [];
  };

  bulkExpireCoupons = async () => {
    const ids = [...this.selectedIds];
    if (ids.length === 0) {
      throw new Error("Select at least one coupon.");
    }

    this.saving = true;
    try {
      const result = await this.swayyCouponAPI.expireCoupons(ids);
      const expiredIds = result.expired_ids ?? [];
      const alreadyExpiredIds = result.already_expired_ids ?? [];
      const missingIds = result.missing_ids ?? [];
      const expiredSet = new Set([...expiredIds, ...alreadyExpiredIds]);
      this.coupons = this.coupons.map((coupon) =>
        expiredSet.has(coupon.id)
          ? { ...coupon, is_expired: true, state: "expired" }
          : coupon,
      );
      if (this.detail && expiredSet.has(this.detail.id)) {
        this.detail = {
          ...this.detail,
          is_expired: true,
          state: "expired",
        };
        await this.refreshOpenActivityLogs();
      }
      await this.getCoupons();
      this.clearSelection();
      this.expireOpen = false;
      return {
        succeeded: expiredIds.length,
        already: alreadyExpiredIds.length,
        failed: missingIds.length,
      };
    } finally {
      this.saving = false;
    }
  };
}

/** Local singleton — do not register in shared stores/index. */
export const swayyCouponStore = new SwayyCouponStore();
